import { FC, useState, useMemo, useRef, JSX } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageSection from "@app/shared/PageSection";
import PageInner from "@app/shared/PageInner";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { ColumnConfig, SortDirections, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { FlyoutLink } from "@app/shared/Flyouts";

import AiResponseDetailPanel from "@app/operations/ai-response-review/AiResponseDetailFlyout";
import AiResponseSummaryFlyout from "@app/operations/ai-response-review/AiResponseSummaryFlyout";
import AiResponseReviewFilters from "@app/operations/ai-response-review/AiResponseReviewFilters";
import AiResponseReviewExport from "@app/operations/ai-response-review/AiResponseReviewExport";
import UtilizationTrendTab from "@app/operations/ai-response-review/UtilizationTrendTab";

import {
  AiResponseReviewQueryModel,
  AiChatMessageType
} from "@app/operations/ai-response-review/interfaces";
import { AI_RESPONSE_REVIEWS_QUERY_KEY, useAiResponseReviews } from "@app/operations/ai-response-review/hooks/useAiResponseReviews";
import { useAiResponseHealthMetrics } from "@app/operations/ai-response-review/hooks/useAiResponseHealthMetrics";
import {
  SortAiResponseReviewColumnEnum,
  SortDirectionEnum
} from "@app/operations/ai-response-review/enums";
import { getDefaultAiResponseReviewFilters } from "@app/operations/ai-response-review/utils/filterDefaults";
import { formatRating } from "@app/operations/ai-response-review/formatRating";
import { formatDateTime } from "@app/shared/formatDateTime";

interface AiResponseReviewTableData {
  key: string;
  createdUtc: string;
  clientId: number;
  clientName: string;
  projectId: number;
  environment: string;
  userId: number;
  userName: string;
  prompt: string;
  semanticKernelTemperature: number;
  searchServiceScoreCutoff: number;
  searchServiceMaximumDefinitions: number;
  responseId: number;
  responseTimeMs: number;
  userRating: string;
  userReason: string;
  internalRating: string;
  internalReason: string;
  internalLastUpdatedUtc: string;
  tableGenerated: string;
  tableDownloaded: string;
  previousSqlLoaded: string;
  actions: JSX.Element;
}

const AiResponseReview: FC = () => {
  const [activeTab, setActiveTab] = useState<'trend' | 'review'>('review');

  // `filters` is the draft the panel edits freely; `appliedFilters` is what has
  // actually been queried. Splitting them is what lets the query key be the single
  // fetch trigger — editing a filter field no longer touches the query.
  const [filters, setFilters] = useState<AiResponseReviewQueryModel>(getDefaultAiResponseReviewFilters);
  // Seeded from the same object so the first render's draft and query agree exactly.
  const [appliedFilters, setAppliedFilters] = useState<AiResponseReviewQueryModel>(filters);

  const { isError, isLoading, reviews, totalCount, isPlaceholderData } = useAiResponseReviews(appliedFilters);
  const { healthMetrics, isLoading: healthMetricsLoading } = useAiResponseHealthMetrics();

  const queryClient = useQueryClient();
  // Waffles keys rows by index, and each row owns the flyout it opens, so refreshing
  // the list under an open flyout rebinds it to whatever response now sits on that
  // row. An edit that drops the response out of the current filter (setting Dev
  // Resolved while filtered to unresolved) would therefore swap the panel the user is
  // reading for a different response. So record that a refresh is owed and run it on
  // close instead, which also avoids refetching when the user edited nothing.
  const pendingListRefresh = useRef(false);

  const handleReviewSaved = () => {
    pendingListRefresh.current = true;
  };

  const handleFlyoutClose = () => {
    if (!pendingListRefresh.current) return;

    pendingListRefresh.current = false;
    queryClient.invalidateQueries({ queryKey: [AI_RESPONSE_REVIEWS_QUERY_KEY] });
  };

  // Paging and sorting take effect immediately, so they move both the draft and the
  // applied query; filter fields wait for Apply.
  const applyImmediately = (change: Partial<AiResponseReviewQueryModel>) => {
    setFilters(prev => ({ ...prev, ...change }));
    setAppliedFilters(prev => ({ ...prev, ...change }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    applyImmediately({ pageNumber: 1, pageSize });
  };

  const handlePageChange = (pageNumber: number) => {
    applyImmediately({ pageNumber });
  };

  const handleApplyFilters = (filtersToApplyOverride?: AiResponseReviewQueryModel) => {
    const filtersToApply = !!filtersToApplyOverride ? filtersToApplyOverride : filters;
    const next = { ...filtersToApply, pageNumber: 1 };
    setFilters(next);
    setAppliedFilters(next);
  };

  // Replaces every filter with just this thread, dropping the default 90-day start
  // date so older responses in the thread are still returned.
  const handleViewThread = (threadId: string) => {
    const next: AiResponseReviewQueryModel = {
      ...getDefaultAiResponseReviewFilters(),
      startDate: undefined,
      threadId
    };

    // The new filters query on their own, so a refresh owed from an edit in the
    // flyout being closed here would only duplicate that request.
    pendingListRefresh.current = false;

    setFilters(next);
    setAppliedFilters(next);
  };

  const paginationProps: WafflesPaginationProps = useMemo<WafflesPaginationProps>(() => {
    return {
      changePage: (pageNumber: number) => handlePageChange(pageNumber),
      enableBackendPagination: true,
      handlePageSizeChange: (pageSize: number) => handlePageSizeChange(pageSize),
      pageSizeOptions: [10, 25, 50, 100],
      recordsPerPage: appliedFilters.pageSize,
      resetToFirstPage: false,
      showPageSizeSelector: true,
      totalRecords: totalCount
    };
  }, [appliedFilters.pageSize, totalCount]);

  const customSort = (data: AiResponseReviewTableData[], column: keyof AiResponseReviewTableData, direction: SortDirections) => {
    let sortColumn: SortAiResponseReviewColumnEnum;

    switch (column) {
      case "createdUtc":
        sortColumn = SortAiResponseReviewColumnEnum.CreatedDate;
        break;
      case "clientId":
        sortColumn = SortAiResponseReviewColumnEnum.Client;
        break;
      case "clientName":
        sortColumn = SortAiResponseReviewColumnEnum.ClientName;
        break;
      case "projectId":
        sortColumn = SortAiResponseReviewColumnEnum.Project;
        break;
      case "environment":
        sortColumn = SortAiResponseReviewColumnEnum.Environment;
        break;
      case "userId":
        sortColumn = SortAiResponseReviewColumnEnum.User;
        break;
      case "userName":
        sortColumn = SortAiResponseReviewColumnEnum.UserName;
        break;
      case "semanticKernelTemperature":
        sortColumn = SortAiResponseReviewColumnEnum.SemanticKernelTemperature;
        break;
      case "searchServiceScoreCutoff":
        sortColumn = SortAiResponseReviewColumnEnum.SearchServiceScoreCutoff;
        break;
      case "searchServiceMaximumDefinitions":
        sortColumn = SortAiResponseReviewColumnEnum.SearchServiceMaximumDefinitions;
        break;
      case "responseId":
        sortColumn = SortAiResponseReviewColumnEnum.ResponseId;
        break;
      case "responseTimeMs":
        sortColumn = SortAiResponseReviewColumnEnum.ResponseTime;
        break;
      case "userRating":
        sortColumn = SortAiResponseReviewColumnEnum.UserRating;
        break;
      case "internalRating":
        sortColumn = SortAiResponseReviewColumnEnum.InternalRating;
        break;
      case "internalLastUpdatedUtc":
        sortColumn = SortAiResponseReviewColumnEnum.LastUpdatedInternal;
        break;
      case "tableGenerated":
        sortColumn = SortAiResponseReviewColumnEnum.TableGenerated;
        break;
      case "tableDownloaded":
        sortColumn = SortAiResponseReviewColumnEnum.TableDownloaded;
        break;
      case "previousSqlLoaded":
        sortColumn = SortAiResponseReviewColumnEnum.PreviousSqlLoaded;
        break;
      default:
        return;
    }

    const backendDirection = direction === SortDirections.Asc
      ? SortDirectionEnum.Ascending
      : SortDirectionEnum.Descending;

    applyImmediately({ pageNumber: 1, sortColumn, sortDirection: backendDirection });

    return data;
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const tableData: AiResponseReviewTableData[] = useMemo(() => {
    /* oxlint-disable sort-keys */
    return reviews.map(item => ({
      key: item.responseId.toString(),
      createdUtc: formatDateTime(item.createdUtc),
      clientId: item.clientId,
      clientName: item.clientName,
      projectId: item.projectId,
      environment: item.environment,
      userId: item.userId,
      userName: item.userName,
      prompt: truncateText(item.prompt, 50),
      semanticKernelTemperature: item.semanticKernelTemperature || 0,
      searchServiceScoreCutoff: item.searchServiceScoreCutoff || 0,
      searchServiceMaximumDefinitions: item.searchServiceMaximumDefinitions || 0,
      responseId: item.responseId,
      responseTimeMs: item.responseTimeMs,
      userRating: formatRating(item.userRating),
      userReason: truncateText(item.userReason || "-", 30),
      internalRating: formatRating(item.internalRating),
      internalReason: truncateText(item.internalReason || "-", 30),
      internalLastUpdatedUtc: item.internalLastUpdatedUtc
        ? formatDateTime(item.internalLastUpdatedUtc)
        : "-",
      tableDownloaded: item.tableDownloaded === true ? "Y" : item.tableDownloaded === false ? "N" : "-",
      tableGenerated: item.messageType === AiChatMessageType.Table ? "Y" : "N",
      previousSqlLoaded: item.previousSqlLoaded === true ? "Y" : item.previousSqlLoaded === false ? "N" : "-",
      actions: (
        <FlyoutLink
          onClose={handleFlyoutClose}
          content={{
            body: (
              <AiResponseDetailPanel
                responseId={item.responseId}
                onViewThread={handleViewThread}
                onSaved={handleReviewSaved}
              />
            ),
            title: `Response ID: ${item.responseId}`
          }}
          testId={`ai-response-review__response-detail-${item.responseId}`}
        >
          <span className="text-primary">View Details</span>
        </FlyoutLink>
      )
    }));
    /* oxlint-enable sort-keys */
  }, [reviews]);

  const columnConfig: ColumnConfig = useMemo<ColumnConfig>(() => {
    return {
      actions: {
        displayText: "Actions",
        noSort: true,
        sizeContentToFit: true,
        testId: "actions"
      },
      clientId: {
        displayText: "Client",
        sizeContentToFit: true,
        testId: "client-id"
      },
      clientName: {
        displayText: "Client Name",
        sizeContentToFit: true,
        testId: "client-name"
      },
      createdUtc: {
        displayText: "Date/Time (UTC)",
        sizeContentToFit: true,
        testId: "created-utc"
      },
      environment: {
        displayText: "Environment",
        sizeContentToFit: true,
        testId: "environment"
      },
      internalLastUpdatedUtc: {
        displayText: "Last Updated",
        sizeContentToFit: true,
        testId: "last-updated"
      },
      internalRating: {
        displayText: "Internal Rating",
        sizeContentToFit: true,
        testId: "internal-rating"
      },
      internalReason: {
        displayText: "Internal Reason",
        noSort: true,
        sizeContentToFit: true,
        testId: "internal-reason"
      },
      model: {
        displayText: "Model",
        sizeContentToFit: true,
        testId: "model"
      },
      previousSqlLoaded: {
        displayText: "Previous Sql Loaded",
        sizeContentToFit: true,
        testId: "previous-sql-loaded"
      },
      projectId: {
        displayText: "Project",
        sizeContentToFit: true,
        testId: "project-id"
      },
      prompt: {
        displayText: "Prompt",
        noSort: true,
        sizeContentToFit: true,
        testId: "prompt"
      },
      responseId: {
        displayText: "Response ID",
        sizeContentToFit: true,
        testId: "response-id"
      },
      responseTimeMs: {
        displayText: "Response Time (ms)",
        sizeContentToFit: true,
        testId: "response-time"
      },
      searchServiceMaximumDefinitions: {
        displayText: "Max Definitions",
        sizeContentToFit: true,
        testId: "search-service-max-definitions"
      },
      searchServiceScoreCutoff: {
        displayText: "Score Cutoff",
        sizeContentToFit: true,
        testId: "search-service-score-cutoff"
      },
      semanticKernelTemperature: {
        displayText: "Temperature",
        sizeContentToFit: true,
        testId: "semantic-kernel-temperature"
      },
      tableDownloaded: {
        displayText: "Table Downloaded",
        sizeContentToFit: true,
        testId: "table-downloaded"
      },
      tableGenerated: {
        displayText: "Table Generated",
        sizeContentToFit: true,
        testId: "table-generated"
      },
      userId: {
        displayText: "User",
        sizeContentToFit: true,
        testId: "user-id"
      },
      userName: {
        displayText: "User Name",
        sizeContentToFit: true,
        testId: "user-name"
      },
      userRating: {
        displayText: "User Rating",
        sizeContentToFit: true,
        testId: "user-rating"
      },
      userReason: {
        displayText: "User Reason",
        noSort: true,
        sizeContentToFit: true,
        testId: "user-reason"
      }
    };
  }, []);

  const trendArrow = (trend: 'up' | 'neutral' | 'down') => {
    if (trend === 'up') return '↑';
    if (trend === 'down') return '↓';
    return '→';
  };

  const renderHealthMetric = (label: string, rate: number, trend: 'up' | 'neutral' | 'down') => {
    const text = `${label}: ${Math.round(rate * 100)}% ${trendArrow(trend)}`;
    return rate < 0.85
      ? <strong className="text-danger">{text}</strong>
      : <span>{text}</span>;
  };

  return (
    <Page documentTitle="Review & Manage AI Responses">
      <Breadcrumbs template={BreadcrumbTemplateEnum.Operations} items={["Review & Manage AI Responses"]} />
      <PageInner>
        <PageHeader title="Review & Manage AI Responses" disableHR={true}>
        </PageHeader>

        <div className="ai-response-review__tabs" role="tablist">
          <button
            type="button"
            id="ai-response-review__tab-trend"
            role="tab"
            aria-selected={activeTab === 'trend'}
            aria-controls="ai-response-review__tabpanel-trend"
            className={`ai-response-review__tab ${activeTab === 'trend' ? 'ai-response-review__tab--active' : ''}`}
            data-testid="ai-response-review__tab-trend"
            onClick={() => setActiveTab('trend')}
          >
            Utilization Trend
          </button>
          <button
            type="button"
            id="ai-response-review__tab-review"
            role="tab"
            aria-selected={activeTab === 'review'}
            aria-controls="ai-response-review__tabpanel-review"
            className={`ai-response-review__tab ${activeTab === 'review' ? 'ai-response-review__tab--active' : ''}`}
            data-testid="ai-response-review__tab-review"
            onClick={() => setActiveTab('review')}
          >
            Review & Manage
          </button>
        </div>

        {activeTab === 'trend' && (
          <div id="ai-response-review__tabpanel-trend" role="tabpanel" aria-labelledby="ai-response-review__tab-trend">
            <PageSection>
              <UtilizationTrendTab />
            </PageSection>
          </div>
        )}

        {activeTab === 'review' && (
          <div id="ai-response-review__tabpanel-review" role="tabpanel" aria-labelledby="ai-response-review__tab-review">
            <PageSection>
              <div className="d-flex justify-content-between align-items-center">
                <AiResponseReviewFilters
                  filters={filters}
                  onFiltersChange={setFilters}
                  onApplyFilters={handleApplyFilters}
                />
                {healthMetricsLoading && (
                  <span className="text-muted">— —</span>
                )}
                {!healthMetricsLoading && healthMetrics && (
                  <div>
                    <div>{renderHealthMetric('SQL Warning-Free (14d)', healthMetrics.sqlWarningFreeRate, healthMetrics.sqlWarningFreeRateTrend)}</div>
                    <div>{renderHealthMetric('Response Success Rate (14d)', healthMetrics.responseSuccessRate, healthMetrics.responseSuccessRateTrend)}</div>
                  </div>
                )}
                <div className="d-flex align-items-center gap-2">
                  <FlyoutLink
                    testId="ai-response-review__summary-flyout"
                    classNames="qes-button-secondary"
                    size="60"
                    content={{
                      body: <AiResponseSummaryFlyout />,
                      title: "Prompt Category Summary"
                    }}
                  >
                    Prompt Category Summary
                  </FlyoutLink>
                  <AiResponseReviewExport filters={appliedFilters} totalCount={totalCount} isPlaceholderData={isPlaceholderData} />
                </div>
              </div>

              {isError && (
                <div className="alert alert-danger" role="alert">
                  Failed to load AI response reviews
                </div>
              )}

              <Waffles<AiResponseReviewTableData>
                columns={columnConfig}
                customSort={customSort}
                data={tableData || []}
                enablePagination={true}
                loading={isLoading}
                paginationProps={paginationProps}
                testId="ai-response-review-grid"
              />
            </PageSection>
          </div>
        )}
      </PageInner>
    </Page>
  );
};

export default AiResponseReview;
